import { array, fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { endOfDay, max, startOfDay } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { restartableTask, task, timeout } from 'ember-concurrency';
import { pluralize } from 'ember-inflector';
import type LinkManagerService from 'ember-link/services/link-manager';
import RouteTemplate from 'ember-route-template';
import { gt } from 'ember-truth-helpers';
import DateRanger from 'garaje/components/date-ranger';
import GroupInviteList from 'garaje/components/invites/table/group-invite-list';
import MultiFlowSelect from 'garaje/components/multi-flow-select';
import Pagination from 'garaje/components/pagination';
import TrialDaysLeft from 'garaje/components/trial-days-left';
import UiHeader from 'garaje/components/ui/header';
import UiPage from 'garaje/components/ui/page';
import UiSearch from 'garaje/components/ui-search';
import type { PaginatedRecordArray } from 'garaje/infinity-models/v3-offset';
import type Flow from 'garaje/models/flow';
import GroupInvite from 'garaje/models/group-invite';
import type Location from 'garaje/models/location';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import { DATE_FNS_YYYY_MM_DD, modifyDateInTimeZone, parseYyyyMmDdInTimeZone } from 'garaje/utils/date-fns-tz-utilities';
import { DebouncedSearchManager } from 'garaje/utils/debounced-search-manager';
import zft from 'garaje/utils/zero-for-tests';

import type VisitorsInvitesGroupsIndexController from './controller';
import type { VisitorsInvitesGroupsIndexRouteModel } from './route';

interface VisitorsInvitesGroupsIndexRouteTemplateSignature {
  Args: {
    model: VisitorsInvitesGroupsIndexRouteModel;
    controller: VisitorsInvitesGroupsIndexController;
  };
}

const DATE_RANGE_SHORTCUTS = [
  'today',
  'next2Days',
  'thisWeek',
  'next7Days',
  'next30Days',
  'thisMonth',
  'past7Days',
  'past30Days',
  'past90Days',
  'monthToDate',
  'yearToDate',
  'pastYear',
  'allTime',
];

class VisitorsInvitesGroupsIndexRouteTemplate extends Component<VisitorsInvitesGroupsIndexRouteTemplateSignature> {
  @service declare store: StoreService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare linkManager: LinkManagerService;
  @service declare metrics: MetricsService;
  @service declare state: StateService;

  @tracked limit = 25;
  @tracked page = 1;
  @tracked query = '';
  @tracked resultsCount = 0;
  @tracked sort = 'expected_arrival_time';

  searchManager!: DebouncedSearchManager;

  constructor(owner: unknown, args: VisitorsInvitesGroupsIndexRouteTemplateSignature['Args']) {
    super(owner, args);
    this.searchManager = new DebouncedSearchManager({
      getCurrentQuery: () => this.args.controller.query,
      setQuery: (value) => {
        this.args.controller.query = value;
      },
      setPage: (page) => {
        this.args.controller.page = page;
      },
      metrics: this.metrics,
      eventName: 'Group Invites Searched',
    });
  }

  dateRangePresetsOrder = DATE_RANGE_SHORTCUTS;

  get location(): Location {
    return this.args.model.currentLocation;
  }

  get showGroupInviteTabs(): boolean {
    return this.featureFlags.isEnabled('visitors-generic-invite-link');
  }

  get newInviteTargetDate(): string {
    const targetDate = max([this.startDateWithDefault, this.todayAsDate]);

    return formatInTimeZone(targetDate, this.timezone, DATE_FNS_YYYY_MM_DD);
  }

  get locationIsDisabled(): boolean {
    return this.state.currentLocation.disabled || this.state.currentLocation.disabledToEmployeesAt !== null;
  }

  get startDateWithDefault(): Date {
    if (this.args.controller.startDate) return new Date(this.args.controller.startDate);

    const date = parseYyyyMmDdInTimeZone(this.args.controller.startDateWithDefault, new Date(), this.timezone);
    const start = modifyDateInTimeZone(date, this.timezone, startOfDay);

    return start;
  }

  get endDateWithDefault(): Date {
    if (this.args.controller.endDate) return new Date(this.args.controller.endDate);

    const date = parseYyyyMmDdInTimeZone(this.args.controller.endDateWithDefault, new Date(), this.timezone);
    const end = modifyDateInTimeZone(date, this.timezone, endOfDay);

    return end;
  }

  get todayAsDate(): Date {
    return parseYyyyMmDdInTimeZone(this.args.controller.todayAsString, new Date(), this.timezone);
  }

  get timezone(): string {
    return this.state.currentLocation.timezone;
  }

  get tabs(): Array<{
    label: string;
    link: ReturnType<LinkManagerService['createUILink']>;
  }> {
    return [
      {
        label: 'Single Invites',
        link: this.linkManager.createUILink({
          route: 'visitors.invites.index',
        }),
      },
      {
        label: 'Group Invites',
        link: this.linkManager.createUILink({
          route: 'visitors.invites.groups.index',
        }),
      },
    ];
  }

  get reloadKey(): string {
    return `${this.args.controller.startDate}-${this.args.controller.endDate}-${this.args.controller.query}-${this.args.controller.selectedFlows}`;
  }

  get selectedFlowModels(): Flow[] {
    const ids = this.args.controller.selectedFlows.split(',').filter((maybeId) => maybeId !== '');
    return ids.map((id) => this.store.peekRecord('flow', id)).filter((maybeFlow) => !!maybeFlow);
  }

  @action
  searchGroupInvites(event: Event): void {
    this.searchManager.handleFormSubmit(event, this.mutateQuery);
  }

  @action
  updateInputQuery(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchManager.handleInputChange(target.value, this.mutateQuery);
  }

  @action
  clearSearch(): void {
    void this.mutateQuery.cancelAll();
    this.args.controller.page = 1;
    this.searchManager.clearSearch();
  }

  mutateQuery = restartableTask(async (query: string, skipLengthCheck: boolean = false) => {
    if (!this.searchManager.shouldSearch(query, skipLengthCheck)) return;

    await timeout(zft(500));

    this.searchManager.executeSearch(query);
  });

  loadGroupInvitesTask = task(
    { restartable: true },
    async (page: number, limit: number): Promise<PaginatedRecordArray<GroupInvite>> => {
      const { sort, location } = this;
      const query = this.args.controller.query;
      const offset = (page - 1) * limit;
      const filter: { [key: string]: unknown } = { location: location.id };

      const { start, end, visitorTypeFilter } = this.args.controller;
      if (start && end) {
        filter['start_datetime'] = start.toISOString();
        filter['end_datetime'] = end.toISOString();
      }

      if (query && query.length > 0) {
        filter['group_name'] = query;
      }

      if (visitorTypeFilter && visitorTypeFilter.length > 0) {
        filter['visitor_type'] = visitorTypeFilter;
      }

      const params: { [key: string]: unknown } = {
        filter,
        page: { offset, limit },
        include: GroupInvite.groupInviteIncludes({ includeInviteTemplates: true }),
      };

      if (sort) params['sort'] = sort;

      const results = <PaginatedRecordArray<GroupInvite>>await this.store.query('group-invite', params);

      this.resultsCount = results.meta.total;
      this.searchManager.showCount = true;

      return results;
    },
  );

  <template>
    <UiPage>
      <div class="flex items-center">
        <div class="flex-auto min-w-0 min-h-0">
          <UiHeader>
            {{#if this.searchManager.showCount}}
              <span data-test-top-group-invites-count>{{this.resultsCount}}</span>
              {{pluralize this.resultsCount "Group Invite" withoutCount=true}}
              {{#if @controller.query}}
                matching "{{@controller.query}}"
              {{/if}}
            {{/if}}
            <TrialDaysLeft @subscription={{@model.vrSubscription}} />
          </UiHeader>
        </div>
        <form
          class="flex-auto min-w-0 min-h-0 text-right ml-4 relative print:hidden"
          data-test-search-form
          {{on "submit" this.searchGroupInvites}}
        >
          <UiSearch
            @inputAriaLabel="Search invites"
            @inputId="invites-search"
            @placeholder="Search group invites"
            @showClearButton={{this.searchManager.showClearButton}}
            @onInput={{this.updateInputQuery}}
            @onClear={{this.clearSearch}}
            @value={{this.searchManager.searchQuery}}
          />

          {{#if this.searchManager.showSearchHint}}
            <label
              for="invites-search"
              class="p-0 m-0 text-grey !text-sm absolute top-1/2 -translate-y-1/2 pointer-events-none
                {{if this.searchManager.showClearButton 'right-9' 'right-4'}}"
              data-test-search-hint
            >
              Press ENTER to search
            </label>
          {{/if}}
        </form>
        <LinkTo
          @route="visitors.invites.new"
          @query={{hash returnTo="visitors.invites.groups.index" type="group" targetDate=this.newInviteTargetDate}}
          class="btn btn--brand ml-2 flex-none {{if this.locationIsDisabled ' disabled'}}"
          aria-disabled="{{this.locationIsDisabled}}"
          data-test-new-invite
        >
          New invite
        </LinkTo>
      </div>

      {{#if this.showGroupInviteTabs}}
        {{#if (gt this.tabs.length 1)}}
          <nav class="tabs !mt-4 mb-6" data-test-tab-nav data-test-invites-tabs aria-label="Invite type navigation">
            {{#each this.tabs as |tab|}}
              <a
                data-test-tab-item={{tab.label}}
                href={{tab.link.url}}
                class="text-sm py-2 inline-block mr-4 {{if tab.link.isActiveWithoutQueryParams 'active'}}"
                {{on "click" tab.link.transitionTo}}
              >
                {{tab.label}}
              </a>
            {{/each}}
          </nav>
        {{/if}}
      {{/if}}

      <div class="pb-4 flex items-center">
        <div class="text-sm flex-auto min-w-0 min-h-0 flex">
          <DateRanger
            @shortcuts={{this.dateRangePresetsOrder}}
            @startDate={{@controller.start}}
            @endDate={{@controller.end}}
            @onDateSelected={{@controller.selectDateRange}}
            @onDateRangeSelected={{@controller.selectDateRange}}
            @timezone={{@controller.timezone}}
            data-test-date-range
          />
        </div>
        <div class="text-right flex items-center">
          {{#if (gt @model.flows.length 1)}}
            <div data-test-flow-filtering class="inline-block mb-0 mr-2 text-left">
              <MultiFlowSelect
                @flows={{@model.flows}}
                @selectedFlows={{this.selectedFlowModels}}
                @selectedFlowsChanged={{@controller.selectFlows}}
                @triggerClass="w-48 truncate"
              />
            </div>
          {{/if}}
        </div>
      </div>

      {{! Use reloadKey to force component recreation when any filter changes (date or search query) }}
      {{#each (array this.reloadKey)}}
        <Pagination
          @page={{@controller.page}}
          @limit={{this.limit}}
          @total={{this.resultsCount}}
          @loadFn={{this.loadGroupInvitesTask.perform}}
          @onPageChange={{fn (mut @controller.page)}}
          as |api|
        >
          <GroupInviteList
            @model={{api.rows}}
            @isLoading={{api.loading}}
            @limit={{this.limit}}
            data-test-group-invites-list
          />
        </Pagination>
      {{/each}}
    </UiPage>
  </template>
}

export default RouteTemplate(VisitorsInvitesGroupsIndexRouteTemplate);
